import React from "react";
import {
  Paper,
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidV4 } from "uuid";
import moment from "moment";
import { message } from "antd";
import { MutatingDots, ProgressBar } from "react-loader-spinner";
import { updateSubmission } from "../actions/submission";
import { Helmet } from "react-helmet-async";
import API from "../Api/api";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import DenseTable from "../ui/DenseTable";
import Autocomplete from "@mui/material/Autocomplete";

const colors = {
  primary: "#21313c",
  primaryLight: "#304753",
  primaryDark: "#19262f",
  secondary: "#17b859",
  secondaryDark: "#109347",
  secondarySoft: "rgba(23,184,89,0.12)",
  background: "#f6f8fa",
  surface: "#ffffff",
  surfaceSoft: "#f8fafc",
  border: "#e4e9ee",
  textPrimary: "#21313c",
  textSecondary: "#667085",
  danger: "#d32f2f",
  dangerDark: "#b71c1c",
  dangerSoft: "rgba(211,47,47,0.08)",
  warningSoft: "rgba(255, 193, 7, 0.12)",
};

const columns = [
  { id: "date", label: "DATE", width: 120 },
  {
    id: "description",
    label: "DESCRIPTION",
    width: 550,
    cellClassName: (params) => {
      const cellValue = params.value;
      return cellValue === "BALANCE PER THEIR BOOKS" ||
        cellValue === "LESS | OUR OUTSTANDING CREDITS" ||
        cellValue === "LESS | THEIR OUTSTANDING CREDITS" ||
        cellValue === "ADD | OUR OUTSTANDING DEBITS" ||
        cellValue === "ADD | THEIR OUTSTANDING DEBITS" ||
        cellValue === "BALANCE PER OUR BOOKS"
        ? "bold-cell"
        : "";
    },
  },
  {
    id: "outstanding",
    label: "OUTSTANDING",
    width: 150,
    align: "center",
    valueFormatter: (params) => {
      const formattedNumber = params.value
        ? Number(params.value).toLocaleString()
        : "";
      return formattedNumber;
    },
  },
  {
    id: "balance",
    label: "BALANCE",
    align: "center",
    width: 150,
    valueFormatter: (params) => {
      const formattedNumber = params.value
        ? Number(params.value).toLocaleString()
        : "";
      return formattedNumber;
    },
  },
  {
    id: "aging",
    label: "AGING",
    headerClassName: "center-header",
    align: "center",
    cellClassName: (params) => {
      const value = params.value;
      return value > 30 ? "red-cell" : "";
    },
    width: 260,
  },
];

export default function Approvals() {
  const locations = useSelector((state) => state.location) || [];
  const dispatch = useDispatch();
  const location = useLocation();
  const user = JSON.parse(secureLocalStorage.getItem("profile"));

  const [reconciliationType, setReconciliationType] = React.useState("double");
  const [primary, setPrimary] = React.useState("head office");
  const [secondary, setSecondary] = React.useState("");
  const [singleLocation, setSingleLocation] = React.useState("");
  const [years, setYears] = React.useState([]);
  const [modalOpen, setModalOpen] = React.useState(false);

  const [branchOutCr, setBranchOutCr] = React.useState([]);
  const [branchOutDr, setBranchOutDr] = React.useState([]);
  const [headOfficeOutCr, setHeadOfficeOutCr] = React.useState([]);
  const [headOfficeOutDr, setHeadOfficeOutDr] = React.useState([]);
  const [singleOutCr, setSingleOutCr] = React.useState([]);
  const [singleOutDr, setSingleOutDr] = React.useState([]);

  const [inputValue, setInputValue] = React.useState("");
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [trigger, setTrigger] = React.useState(false);

  const [loader] = React.useState(false);
  const [fetching] = React.useState(false);
  const [balance, setBalance] = React.useState([]);
  const [unapproved, setUnapproved] = React.useState([]);

  const [deleteMsg, setDeleteMsg] = React.useState("");
  const [deleteStatus, setDeleteStatus] = React.useState(false);
  const [confirmDeclineText, setConfirmDeclineText] = React.useState("");

  const approverName = `${user?.results?.firstName || ""} ${
    user?.results?.lastName || ""
  }`.trim();

  const handleClose = () => {
    setModalOpen(false);
    setConfirmDeclineText("");
    setDeleteMsg("");
  };

  const handleOpen = () => {
    setModalOpen(true);
    setConfirmDeclineText("");
    setDeleteMsg("");
  };

  React.useEffect(() => {
    const branch =
      reconciliationType === "single"
        ? singleLocation
        : primary && secondary
          ? primary + " " + secondary
          : "";

    if (!branch) return;

    let fetchingId;

    const fetchDates = async () => {
      try {
        fetchingId = toast.loading("Fetching available dates....");
        const res = await API.get(
          `/api/v1/reconciliation-submission?branch=${branch}&approved=false`,
        );

        if (res.data) {
          setYears(res.data);
          toast.remove(fetchingId);
          toast.success("Dates Loaded");
        } else {
          setYears([]);
          setInputValue("");
          toast.remove(fetchingId);
        }
      } catch (error) {
        toast.remove(fetchingId);
        toast.error(error.message);
      }
    };

    fetchDates();
  }, [reconciliationType, primary, secondary, singleLocation]);

  function analyData(label) {
    return { label };
  }

  const fetchData = async () => {
    let fetchingId;

    try {
      if (reconciliationType === "single") {
        if (!singleLocation || !inputValue) {
          setTrigger(false);
          toast.error("Please fill in required fields");
          return;
        }

        fetchingId = toast.loading("Generating report....");

        const fetchSingleOutCr = await API.get(
          `/api/v1/outstanding-credit?location=primary&branch=${singleLocation}&submission_date=${inputValue}`,
        );

        const fetchSingleOutDr = await API.get(
          `/api/v1/outstanding-debit?location=primary&branch=${singleLocation}&submission_date=${inputValue}`,
        );

        const balance = await API.get(
          `/api/v1/reconciliation-submission?branch=${singleLocation}&submission_date=${inputValue}`,
        );

        setBalance(balance.data);
        setSingleOutCr(fetchSingleOutCr.data);
        setSingleOutDr(fetchSingleOutDr.data);
        setTrigger(true);

        toast.remove(fetchingId);
        toast.success("Report successfully generated");
        return;
      }

      if (primary && secondary && inputValue) {
        fetchingId = toast.loading("Generating report....");

        const fetchBranchOutCr = await API.get(
          `/api/v1/outstanding-credit?location=secondary&branch=${
            secondary + " " + primary
          }&submission_date=${inputValue}`,
        );

        const fetchBranchOutDr = await API.get(
          `/api/v1/outstanding-debit?location=secondary&branch=${
            secondary + " " + primary
          }&submission_date=${inputValue}`,
        );

        const balance = await API.get(
          `/api/v1/reconciliation-submission?branch=${
            primary + " " + secondary
          }&submission_date=${inputValue}`,
        );

        setBalance(balance.data);

        const fetchHeadOfficeOutCr = await API.get(
          `/api/v1/outstanding-credit?location=primary&branch=${
            primary + " " + secondary
          }&submission_date=${inputValue}`,
        );

        const fetchHeadOfficeOutDr = await API.get(
          `/api/v1/outstanding-debit?location=primary&branch=${
            primary + " " + secondary
          }&submission_date=${inputValue}`,
        );

        setBranchOutCr(fetchBranchOutCr.data);
        setBranchOutDr(fetchBranchOutDr.data);
        setHeadOfficeOutCr(fetchHeadOfficeOutCr.data);
        setHeadOfficeOutDr(fetchHeadOfficeOutDr.data);
        setTrigger(true);

        toast.remove(fetchingId);
        toast.success("Report successfully generated");
      } else {
        setTrigger(false);
        toast.remove(fetchingId);
        toast.error("Please fill in required fields");
      }
    } catch (error) {
      toast.remove(fetchingId);
      toast.error(error.message);
    }
  };

  const handleChangePrimary = (event) => {
    setPrimary(event.target.value);
  };

  const handleChangeSecondary = (event) => {
    setSecondary(event.target.value);
  };

  const handleChangeReconciliationType = (event, newType) => {
    if (!newType) return;
    setReconciliationType(newType);
    setTrigger(false);
    setInputValue("");
    setYears([]);
  };

  const handleChangeSingleLocation = (event) => {
    setSingleLocation(event.target.value);
  };

  // A two-statement submission stores "primary secondary" in branch and
  // "secondary primary" in second_branch; find the location names that fit.
  const splitPair = (item) => {
    const names = locations.map((loc) => loc.name);
    for (const p of names) {
      if (!item.branch.startsWith(p + " ")) continue;
      const s = item.branch.slice(p.length + 1);
      if (item.second_branch === `${s} ${p}`) {
        return { primary: p, secondary: s };
      }
    }
    const words = item.branch.split(" ");
    return {
      primary: words.slice(0, 2).join(" "),
      secondary: words.slice(2).join(" "),
    };
  };

  // Clicking a pending report fills in the filters and opens it
  const [autoView, setAutoView] = React.useState(false);
  // While a report is open the list folds away to give the table room
  const [pendingExpanded, setPendingExpanded] = React.useState(false);
  const showPendingList = !trigger || pendingExpanded;
  const openPending = (item) => {
    setPendingExpanded(false);
    const date = moment(item.submission_date).format("YYYY-MM-DD");
    setTrigger(false);
    if (item.branch === item.second_branch) {
      setReconciliationType("single");
      setSingleLocation(item.branch);
    } else {
      const pair = splitPair(item);
      setReconciliationType("double");
      setPrimary(pair.primary);
      setSecondary(pair.secondary);
    }
    setInputValue(date);
    setAutoView(true);
  };
  React.useEffect(() => {
    if (!autoView) return;
    setAutoView(false);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoView]);

  function createData(id, date, description, outstanding, submission_date) {
    let aging;
    if (date && submission_date) {
      aging = moment(submission_date).diff(moment(date), "days");
    }

    return {
      id,
      date,
      description,
      outstanding,
      aging,
      submission_date,
    };
  }

  const branchOutCrRow = branchOutCr.map((d) =>
    createData(
      d._id,
      new Date(d.value_date).toLocaleDateString(),
      d.description,
      d.credit_amt,
      d.submission_date,
    ),
  );

  const headOfficeOutCrRow = headOfficeOutCr.map((d) =>
    createData(
      d._id,
      new Date(d.value_date).toLocaleDateString(),
      d.description,
      d.credit_amt,
      d.submission_date,
    ),
  );

  const branchOutDrRow = branchOutDr.map((d) =>
    createData(
      d._id,
      new Date(d.value_date).toLocaleDateString(),
      d.description,
      d.debit_amt,
      d.submission_date,
    ),
  );

  const headOfficeOutDrRow = headOfficeOutDr.map((d) =>
    createData(
      d._id,
      new Date(d.value_date).toLocaleDateString(),
      d.description,
      d.debit_amt,
      d.submission_date,
    ),
  );

  const accountType = [];
  const ourOutStandingCredit = [];
  const theirOutStandingCredit = [];
  const ourOutStandingDebit = [];
  const theirOutStandingDebit = [];

  branchOutCr.forEach((d) => {
    accountType.push(d.account_type);
    theirOutStandingCredit.push(d.credit_amt);
  });

  branchOutDr.forEach((d) => {
    accountType.push(d.account_type);
    theirOutStandingDebit.push(d.debit_amt);
  });

  headOfficeOutDr.forEach((d) => {
    accountType.push(d.account_type);
    ourOutStandingDebit.push(d.debit_amt);
  });

  headOfficeOutCr.forEach((d) => {
    accountType.push(d.account_type);
    ourOutStandingCredit.push(d.credit_amt);
  });

  const ourOutStandingCreditTotal = ourOutStandingCredit.reduce(
    (acc, val) => acc + val,
    0,
  );

  const theirOutStandingCreditTotal = theirOutStandingCredit.reduce(
    (acc, val) => acc + val,
    0,
  );

  const ourOutStandingDebitTotal = ourOutStandingDebit.reduce(
    (acc, val) => acc + val,
    0,
  );

  const theirOutStandingDebitTotal = theirOutStandingDebit.reduce(
    (acc, val) => acc + val,
    0,
  );

  const rows = [
    { id: uuidV4(), date: "", description: "ASANTE AKYEM COMMUNITY BANK PLC" },
    {
      id: uuidV4(),
      date: "",
      description: `RECONCILIATION OF ${secondary.toUpperCase()} BRANCH WITH ${primary.toUpperCase()} AS AT ${inputValue}`,
    },
    { id: uuidV4(), date: "", description: "" },
    {
      id: uuidV4(),
      date: "",
      description: "BALANCE PER THEIR BOOKS",
      balance: balance[0] ? balance[0].secondary_balance : 0,
    },
    { id: uuidV4(), date: "", description: "" },
    {
      id: uuidV4(),
      date: "",
      description: `${
        accountType[0] === "credit account" ? "LESS" : "ADD"
      } | OUR OUTSTANDING CREDITS`,
    },
    ...headOfficeOutCrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: ourOutStandingCreditTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: `${
        accountType[0] === "credit account" ? "LESS" : "ADD"
      } | THEIR OUTSTANDING CREDITS`,
    },
    ...branchOutCrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: theirOutStandingCreditTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: `${
        accountType[0] === "credit account" ? "ADD" : "LESS"
      } | OUR OUTSTANDING DEBITS`,
    },
    ...headOfficeOutDrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: ourOutStandingDebitTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: `${
        accountType[0] === "credit account" ? "ADD" : "LESS"
      } | THEIR OUTSTANDING DEBITS`,
    },
    ...branchOutDrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: theirOutStandingDebitTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: "BALANCE PER OUR BOOKS",
      balance: balance[0] ? balance[0].primary_balance : 0,
    },
  ];

  const singleOutCrRow = singleOutCr.map((d) =>
    createData(
      d._id,
      new Date(d.value_date).toLocaleDateString(),
      d.description,
      d.credit_amt,
      d.submission_date,
    ),
  );

  const singleOutDrRow = singleOutDr.map((d) =>
    createData(
      d._id,
      new Date(d.value_date).toLocaleDateString(),
      d.description,
      d.debit_amt,
      d.submission_date,
    ),
  );

  const singleOutCreditTotal = singleOutCr.reduce(
    (acc, val) => acc + val.credit_amt,
    0,
  );

  const singleOutDebitTotal = singleOutDr.reduce(
    (acc, val) => acc + val.debit_amt,
    0,
  );

  const singleRows = [
    { id: uuidV4(), date: "", description: "ASANTE AKYEM COMMUNITY BANK PLC" },
    {
      id: uuidV4(),
      date: "",
      description: `SINGLE ACCOUNT RECONCILIATION FOR ${singleLocation.toUpperCase()} AS AT ${inputValue}`,
    },
    { id: uuidV4(), date: "", description: "" },
    {
      id: uuidV4(),
      date: "",
      description: "BALANCE AT PERIOD START",
      balance: balance[0] ? balance[0].secondary_balance : 0,
    },
    { id: uuidV4(), date: "", description: "" },
    { id: uuidV4(), date: "", description: "ADD | OUTSTANDING CREDITS" },
    ...singleOutCrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: singleOutCreditTotal,
    },
    { id: uuidV4(), date: "", description: "LESS | OUTSTANDING DEBITS" },
    ...singleOutDrRow,
    {
      id: uuidV4(),
      date: "",
      description: "",
      balance: singleOutDebitTotal,
    },
    {
      id: uuidV4(),
      date: "",
      description: "COMPUTED BALANCE AT PERIOD END",
      balance: balance[0] ? balance[0].primary_balance : 0,
    },
  ];

  const dates = years.map((data) =>
    analyData(moment(data.submission_date).format("YYYY-MM-DD")),
  );
  const fetchUnapprovedReports = async ({ showToast = true } = {}) => {
    let fetchingId;

    try {
      if (showToast) {
        fetchingId = toast.loading("Loading unapproved locations...");
      }

      const res = await API.get(
        `/api/v1/reconciliation-submission?approved=false`,
      );

      const list = Array.isArray(res.data) ? res.data : [];

      setUnapproved(list);

      if (list.length === 0) {
        setInputValue("");
        message.info("There is no pending report for approval");
      }

      if (showToast) {
        toast.remove(fetchingId);
        toast.success("Pending list updated");
      }

      return list;
    } catch (error) {
      if (showToast) toast.remove(fetchingId);
      toast.error(error?.message || "Failed to load pending reports");
      return [];
    }
  };
  React.useEffect(() => {
    fetchUnapprovedReports();
  }, [location]);
  async function approveReconciliation() {
    const isSingle = reconciliationType === "single";
    const branch = isSingle ? singleLocation : primary + " " + secondary;
    const date = inputValue;

    const data = {
      approved: "true",
      approved_by: approverName,
    };

    if (isSingle) {
      if (!singleLocation || !inputValue) {
        toast.error("More information required to approve");
        return;
      }
    } else if (!primary || !secondary || !inputValue) {
      toast.error("More information required to approve");
      return;
    }

    await dispatch(updateSubmission(branch, date, data));
    await fetchUnapprovedReports({ showToast: false });
    setTrigger(false);
    if (isSingle) {
      setSingleLocation("");
    } else {
      setSecondary("");
    }
  }

  const declineReconciliation = async () => {
    try {
      if (confirmDeclineText !== "DECLINE") {
        toast.error('Type "DECLINE" to confirm');
        return;
      }

      const isSingle = reconciliationType === "single";

      if (isSingle && singleLocation && inputValue) {
        const query = {
          branch: singleLocation,
          submission_date: inputValue,
        };

        setDeleteStatus(true);
        setDeleteMsg("Deleting report...");

        await API.delete(
          `/api/v1/outstanding-debit/${query.branch}/${query.submission_date}`,
          query,
        );

        await API.delete(
          `/api/v1/outstanding-credit/${query.branch}/${query.submission_date}`,
          query,
        );

        await API.delete(
          `/api/v1/reconciliation-submission/${query.branch}/${query.submission_date}`,
          query,
        );

        setDeleteMsg("Successfully declined");
        setDeleteStatus(false);
        handleClose();
        setTrigger(false);
        toast.success("Report declined successfully");
      } else if (!isSingle && primary && secondary && inputValue) {
        const query = {
          branch: primary + " " + secondary,
          submission_date: inputValue,
        };

        const query2 = {
          branch: secondary + " " + primary,
          submission_date: inputValue,
        };

        setDeleteStatus(true);
        setDeleteMsg("Deleting report...");

        await API.delete(
          `/api/v1/outstanding-debit/${query.branch}/${query.submission_date}`,
          query,
        );

        await API.delete(
          `/api/v1/outstanding-debit/${query2.branch}/${query2.submission_date}`,
          query2,
        );

        await API.delete(
          `/api/v1/outstanding-credit/${query.branch}/${query.submission_date}`,
          query,
        );

        await API.delete(
          `/api/v1/outstanding-credit/${query2.branch}/${query2.submission_date}`,
          query2,
        );

        await API.delete(
          `/api/v1/reconciliation-submission/${query.branch}/${query.submission_date}`,
          query,
        );

        setDeleteMsg("Successfully declined");
        setDeleteStatus(false);
        handleClose();
        setTrigger(false);
        toast.success("Report declined successfully");
      } else {
        toast.error("Please fill in required fields");
        handleClose();
      }
    } catch (error) {
      toast.error("Error while declining");
      setDeleteStatus(false);
      setDeleteMsg("");
      handleClose();
    }
  };

  return (
    <Box>
      <Helmet>
        <title>Approvals | Reconciliation</title>
      </Helmet>

      <Dialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px", width: 440 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Approve this report?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.textSecondary, textTransform: "capitalize" }}>
            {reconciliationType === "single"
              ? singleLocation
              : `${primary} ↔ ${secondary}`}{" "}
            · as at {inputValue}
          </Typography>
          <Typography sx={{ mt: 1, color: colors.textSecondary }}>
            It will be recorded as approved by {approverName || "you"}.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setApproveOpen(false)}
            sx={{ textTransform: "none", color: colors.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={<VerifiedOutlinedIcon />}
            onClick={async () => {
              setApproveOpen(false);
              await approveReconciliation();
            }}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              backgroundColor: colors.secondary,
              "&:hover": { backgroundColor: colors.secondaryDark },
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={modalOpen} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 430 },
            bgcolor: colors.surface,
            borderRadius: "20px",
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
              color: "#fff",
              p: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <WarningAmberRoundedIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Decline Confirmation
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  backgroundColor: colors.surfaceSoft,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Typography sx={{ fontWeight: 700, color: colors.textPrimary }}>
                  {approverName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary }}
                >
                  You are about to permanently remove this reconciliation
                  report.
                </Typography>
              </Box>

              <Typography sx={{ color: colors.textPrimary }}>
                Type <strong>DECLINE</strong> below to continue.
              </Typography>

              <TextField
                fullWidth
                size="small"
                label='Type "DECLINE" to confirm'
                value={confirmDeclineText}
                onChange={(e) => setConfirmDeclineText(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: colors.surfaceSoft,
                    "&.Mui-focused fieldset": {
                      borderColor: colors.danger,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.danger,
                  },
                }}
              />

              {deleteMsg ? (
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary }}
                >
                  {deleteMsg}
                </Typography>
              ) : null}

              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={1.5}
              >
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                    flex: 1,
                    borderColor: colors.border,
                    color: colors.textPrimary,
                  }}
                >
                  Cancel
                </Button>

                <Button
                  onClick={declineReconciliation}
                  disabled={deleteStatus || confirmDeclineText !== "DECLINE"}
                  variant="contained"
                  startIcon={<CheckIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    fontWeight: 700,
                    flex: 1,
                    backgroundColor: colors.danger,
                    "&:hover": {
                      backgroundColor: colors.dangerDark,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#f1f3f5",
                      color: "#98a2b3",
                    },
                  }}
                >
                  Decline Report
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Modal>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          useFlexGap
          spacing={2}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={reconciliationType}
            onChange={handleChangeReconciliationType}
            sx={{
              alignSelf: "flex-start",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px !important",
                px: 2,
              },
            }}
          >
            <ToggleButton value="double">Two Statements</ToggleButton>
            <ToggleButton value="single">Single Statement</ToggleButton>
          </ToggleButtonGroup>

          <Box
            sx={{ flex: 1, minWidth: { xs: "100%", md: 600 } }}
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
              lg:
                reconciliationType === "single"
                  ? "1fr 1fr auto"
                  : "1fr 1fr 1fr auto",
            }}
            gap={2}
            alignItems="center"
          >
            {reconciliationType === "single" ? (
              <FormControl fullWidth size="small">
                <InputLabel required id="single-location-label">
                  Location
                </InputLabel>
                <Select
                  labelId="single-location-label"
                  value={singleLocation}
                  label="Location"
                  onChange={handleChangeSingleLocation}
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: colors.surfaceSoft,
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {locations?.map((data) => (
                    <MenuItem key={data._id} value={data.name}>
                      {data.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel required id="primary-location-label">
                    Primary Location
                  </InputLabel>
                  <Select
                    labelId="primary-location-label"
                    value={primary}
                    label="Primary Location"
                    onChange={handleChangePrimary}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: colors.surfaceSoft,
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {locations?.map((data) => (
                      <MenuItem key={data._id} value={data.name}>
                        {data.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel required id="secondary-location-label">
                    Secondary Location
                  </InputLabel>
                  <Select
                    labelId="secondary-location-label"
                    value={secondary}
                    label="Secondary Location"
                    onChange={handleChangeSecondary}
                    sx={{
                      borderRadius: "12px",
                      backgroundColor: colors.surfaceSoft,
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {locations?.map((data) => (
                      <MenuItem key={data._id} value={data.name}>
                        {data.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {loader ? (
              <Box
                sx={{
                  width: "100%",
                  minHeight: 56,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: colors.textSecondary }}
                >
                  searching for available dates
                </Typography>
                <MutatingDots
                  height="60"
                  width="90"
                  color={colors.secondary}
                  secondaryColor={colors.primary}
                  radius="12.5"
                  ariaLabel="mutating-dots-loading"
                  visible={true}
                />
              </Box>
            ) : (
              <Autocomplete
                size="small"
                disablePortal
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                options={dates}
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    label="Available Dates"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: colors.surfaceSoft,
                        "&.Mui-focused fieldset": {
                          borderColor: colors.secondary,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: colors.secondary,
                      },
                    }}
                  />
                )}
              />
            )}

            {fetching ? (
              <Box
                sx={{
                  minWidth: 80,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <ProgressBar
                  height="60"
                  width="60"
                  ariaLabel="progress-bar-loading"
                  borderColor={colors.primary}
                  barColor={colors.secondary}
                />
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={fetchData}
                startIcon={<CompareArrowsOutlinedIcon />}
                sx={{
                  textTransform: "none",
                  color: "#fff",
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                  borderRadius: "12px",
                  fontWeight: 700,
                  height: 40,
                  px: 2.5,
                  boxShadow: "0 10px 18px rgba(33,49,60,0.18)",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primary} 100%)`,
                  },
                }}
              >
                View
              </Button>
            )}

          </Box>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: showPendingList ? 1.2 : 0 }}
        >
          <Typography sx={{ fontWeight: 700, color: colors.textPrimary }}>
            Waiting for approval
          </Typography>
          <Chip
            size="small"
            label={unapproved.length}
            sx={{ fontWeight: 700, backgroundColor: colors.warningSoft }}
          />
          <Box sx={{ flex: 1 }} />
          {trigger && unapproved.length > 0 && (
            <Button
              size="small"
              onClick={() => setPendingExpanded((v) => !v)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {pendingExpanded ? "Hide list" : "Show list"}
            </Button>
          )}
        </Stack>
        {!showPendingList ? null : unapproved.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: colors.textSecondary }}>
            Nothing is waiting for approval.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
              gap: 0.8,
              // About three rows show; more scroll inside the box
              maxHeight: 176,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {unapproved.map((item) => {
              const isSingle = item.branch === item.second_branch;
              const pair = isSingle ? null : splitPair(item);
              return (
                <Box
                  key={item._id || `${item.branch}-${item.submission_date}`}
                  component="button"
                  type="button"
                  onClick={() => openPending(item)}
                  title={`Prepared by ${item.prepared_by || "—"}`}
                  sx={{
                    textAlign: "left",
                    cursor: "pointer",
                    px: 1.2,
                    py: 0.7,
                    minWidth: 0,
                    borderRadius: "10px",
                    border: `1px solid ${colors.border}`,
                    background: colors.surfaceSoft,
                    font: "inherit",
                    transition: "border-color 0.15s, background 0.15s",
                    "&:hover, &:focus-visible": {
                      borderColor: colors.secondary,
                      background: colors.secondarySoft,
                      outline: "none",
                    },
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: 700,
                      fontSize: 13,
                      lineHeight: 1.3,
                      color: colors.textPrimary,
                      textTransform: "capitalize",
                    }}
                  >
                    {isSingle
                      ? item.branch
                      : `${pair.primary} ↔ ${pair.secondary}`}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{ fontSize: 11.5, lineHeight: 1.3, color: colors.textSecondary }}
                  >
                    {isSingle ? "Single" : "Two statements"} ·{" "}
                    {moment(item.submission_date).format("D MMM YYYY")} ·{" "}
                    {item.prepared_by || "—"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {trigger ? (
        <Paper
          elevation={0}
          sx={{
            p: 1,
            borderRadius: "14px",
            border: `1px solid ${colors.border}`,
            background: colors.surface,
          }}
        >
          <DenseTable
            rows={reconciliationType === "single" ? singleRows : rows}
            headers={columns}
            filename="reconciliation report approval"
            height="max(320px, calc(100dvh - 420px))"
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="center"
            spacing={2}
            sx={{ mt: 1, mb: 0.5 }}
          >
            <Button
              onClick={() => setApproveOpen(true)}
              variant="contained"
              startIcon={<VerifiedOutlinedIcon />}
              sx={{
                textTransform: "none",
                minWidth: 180,
                borderRadius: "12px",
                fontWeight: 700,
                background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
                boxShadow: "0 10px 18px rgba(23,184,89,0.22)",
                "&:hover": {
                  background: `linear-gradient(135deg, ${colors.secondaryDark} 0%, #0d7a3b 100%)`,
                },
              }}
            >
              Approve
            </Button>

            <Button
              onClick={handleOpen}
              variant="contained"
              startIcon={<WarningAmberRoundedIcon />}
              sx={{
                textTransform: "none",
                minWidth: 180,
                borderRadius: "12px",
                fontWeight: 700,
                backgroundColor: colors.danger,
                "&:hover": {
                  backgroundColor: colors.dangerDark,
                },
              }}
            >
              Decline
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Box>
          <Paper
            elevation={0}
            sx={{
              minHeight: "58vh",
              borderRadius: "22px",
              border: "1px dashed #cbd5df",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
            }}
          >
            <Box sx={{ textAlign: "center", maxWidth: 760 }}>
              <Box
                sx={{
                  width: 84,
                  height: 84,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "24px",
                  background: colors.warningSoft,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PendingActionsOutlinedIcon
                  sx={{ fontSize: 40, color: colors.primary }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: colors.textPrimary,
                  mb: 1,
                }}
              >
                View Pending Reconciliation Report
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.8,
                  fontSize: "1rem",
                }}
              >
                {reconciliationType === "single"
                  ? "Select a location and available reconciliation date to view a report for approval."
                  : "Select your primary location, secondary location, and available reconciliation date to view a report for approval."}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
